/**
 * useUserPhoto
 *
 * Resolves a user's Microsoft Graph profile photo through the app's AUTHENTICATED
 * api client. The photo endpoint (`GET /identity/users/{id}/photo`) requires a
 * bearer token, so it can't be used as a bare `<img src>` — we fetch it as a blob
 * via the same apiClient instance organizationService uses (bearer attached by the
 * request interceptor) and hand the object URL to `<AvatarImage>`.
 *
 * A module-level cache keyed by userId stores the object URL (or `null` for a
 * 404 / no-photo) so each photo is fetched at most once per session. On any
 * error we cache `null` and return `undefined`, letting `<AvatarFallback>` render
 * initials.
 */

import { useEffect, useState } from 'react';
import { apiClient } from '@/services/apiClient';

// userId -> object URL (has a photo) | null (404 / no photo / error).
// Object URLs are intentionally never revoked: the cache is session-scoped and
// revoking would break every Avatar still pointing at the URL.
const photoCache = new Map<number, string | null>();
// Shared in-flight promises so concurrent callers for the same user fetch once.
const inflight = new Map<number, Promise<string | null>>();

async function fetchPhoto(userId: number): Promise<string | null> {
  try {
    const res = await apiClient.get<Blob>(`/identity/users/${userId}/photo`, {
      responseType: 'blob',
    });
    // 204 (no photo — the common case) comes back as an empty body: don't
    // build an object URL from it. Any non-image/empty -> initials fallback.
    const blob = res.data;
    if (res.status === 204 || !blob || blob.size === 0) {
      photoCache.set(userId, null);
      return null;
    }
    const url = URL.createObjectURL(blob);
    photoCache.set(userId, url);
    return url;
  } catch {
    // Any transient error -> initials fallback.
    photoCache.set(userId, null);
    return null;
  }
}

export function useUserPhoto(userId?: number | null): string | undefined {
  const [url, setUrl] = useState<string | undefined>(() => {
    if (userId == null) return undefined;
    return photoCache.get(userId) ?? undefined;
  });

  useEffect(() => {
    if (userId == null) {
      setUrl(undefined);
      return;
    }

    if (photoCache.has(userId)) {
      setUrl(photoCache.get(userId) ?? undefined);
      return;
    }

    let active = true;
    let promise = inflight.get(userId);
    if (!promise) {
      promise = fetchPhoto(userId).finally(() => inflight.delete(userId));
      inflight.set(userId, promise);
    }
    promise.then((result) => {
      if (active) setUrl(result ?? undefined);
    });

    return () => {
      active = false;
    };
  }, [userId]);

  return url;
}

export default useUserPhoto;
